import {
  getGameActive,
  getPlayersSearch,
  LineupPositions,
  postDataTag,
  postGameActiveLineups,
  postGameByGameIdEnd,
  postGameByGameIdSubstitute,
  postOrganizationsByOrgIdPlayersByPlayerExternalIdEdit,
  postRostersByRosterIdEdit,
  postRostersByRosterIdPlayers,
  PostRostersByRosterIdPlayersData,
  TagDataIngest,
} from "@aura-frontend/omnibus-az-client";
import { useQueryClient } from "@tanstack/react-query";

import { removeOrgPrefix } from "@/lib/utils";
import { getHomeAwayLineups } from "@/pages/Tagging/utils/api-helpers";

import { TeamSide } from "../constants/teams";
import {
  GameFullResponse,
  LineUpPlayer,
  ResultingState,
} from "../types/gameFull";
import {
  buildLastPitchRetag,
  buildPlayerFillPayload,
  buildPlayerTagPayload,
  buildStateTagPayload,
  buildTagPayload,
  TagPlayerOverrides,
} from "../utils/buildTagPayload";
import { GameStateEdit } from "../utils/pitchTagging";
import { PlayerEdit } from "../utils/playerEdit";
import {
  findStoredLineupEntry,
  getMemberExternalId,
  isLocalPlayer,
  pairAddedRosterMembers,
  playerPublicToLineUpPlayer,
  RosterMember,
  StoredLineups,
  toLineupEntry,
  toLineupPair,
  toRosterPlayerInfo,
  toSideType,
} from "./omnibusMappers";
import { TEAM_ROSTER_QUERY_KEY, TeamRosterData } from "./teamRoster";

/**
 * Omnibus writes for Scorekeeping V2 lineup management and at-bat editing.
 * All game writes go through omnibus (never the game service directly):
 * omnibus publishes to the broker, which keeps the game service in sync.
 * Each function throws on failure; callers show the error.
 */

type GameWritesParams = {
  /** Omnibus game id (machine.currentGame.gameUuid) */
  gameId: string | undefined;
  /** The SmartPark the game is on */
  machineUuid: string;
  rosters: Record<TeamSide, TeamRosterData | undefined>;
};

/** POST /data/tag */
const sendTag = async (body: TagDataIngest) => {
  await postDataTag({ body, throwOnError: true });
};

/**
 * Attaches a player's omnibus record when they came from somewhere without it
 * (e.g. the game service's other players), so the roster add doesn't
 * overwrite their details. Looked up in the roster's org; a player who isn't
 * there is sent as the game knows them.
 */
const withOmnibusRecord = async (
  player: LineUpPlayer,
  visibilityGroup: string,
): Promise<LineUpPlayer> => {
  if (player.omnibus || isLocalPlayer(player)) return player;
  const { data } = await getPlayersSearch({
    query: { visibilityGroup, externalId: player.playerId },
    throwOnError: true,
  });
  const found = data?.players?.find((p) =>
    p.external_ids.includes(player.playerId),
  );
  return found
    ? { ...player, omnibus: playerPublicToLineUpPlayer(found).omnibus }
    : player;
};

export const useGameWrites = ({
  gameId,
  machineUuid,
  rosters,
}: GameWritesParams) => {
  const queryClient = useQueryClient();

  const requireGameId = () => {
    if (!gameId) throw new Error("This SmartPark has no active game");
    return gameId;
  };

  /** The team's record plus its loaded players, for roster writes */
  const requireRoster = (side: TeamSide) => {
    const data = rosters[side];
    if (!data?.record) {
      throw new Error(`Couldn't find the ${side} team's roster`);
    }
    return { ...data.record, players: data.roster.players };
  };

  const fetchStoredLineups = async (): Promise<StoredLineups> => {
    const { data } = await getGameActive({
      query: { machineUuid },
      throwOnError: true,
    });
    return getHomeAwayLineups(data.game);
  };

  const invalidateRosters = () =>
    queryClient.invalidateQueries({ queryKey: [TEAM_ROSTER_QUERY_KEY] });

  /**
   * Adds anyone not yet on the team's roster: existing players are linked by
   * id, new (local) players are created. Returns the players with real ids,
   * read from the roster the add returns (never matched by name: players can
   * share one).
   */
  const ensureOnRoster = async (side: TeamSide, players: LineUpPlayer[]) => {
    const roster = requireRoster(side);
    const rosterIds = new Set(roster.players.map((p) => p.playerId));
    const missing = await Promise.all(
      players
        .filter((p) => !rosterIds.has(p.playerId))
        .map((p) => withOmnibusRecord(p, roster.orgId)),
    );
    if (missing.length === 0) return players;

    const { data } = await postRostersByRosterIdPlayers({
      path: { roster_id: roster.rosterId },
      body: {
        player_info: missing.map(toRosterPlayerInfo),
      } as unknown as PostRostersByRosterIdPlayersData["body"],
      throwOnError: true,
    });
    // Typed as a message, but the route returns the updated roster too
    const members =
      (data as { roster?: { unofficial_members?: RosterMember[] } })?.roster
        ?.unofficial_members ?? [];
    const added = pairAddedRosterMembers(missing, members);
    await invalidateRosters();

    const byId = new Map(missing.map((p, i) => [p.playerId, added[i]]));
    return players.map((p) => byId.get(p.playerId) ?? p);
  };

  /** findStoredLineupEntry, or the rebuilt entry if the game can't be read. */
  const toStoredOutEntry = async (
    side: TeamSide,
    outPlayer: LineUpPlayer,
    position: number | LineupPositions,
  ) => {
    const rebuilt = toLineupEntry(outPlayer, position);
    try {
      const stored = await fetchStoredLineups();
      return findStoredLineupEntry(stored[side], rebuilt);
    } catch (error) {
      console.warn(
        "[scorekeeping] couldn't read the stored lineup for a substitution",
        error,
      );
      return rebuilt;
    }
  };

  /**
   * Puts `inPlayer` into `position`, replacing `outPlayer` when given. With no
   * outgoing player omnibus appends them (used for new batting order slots).
   */
  const substitute = async ({
    side,
    inPlayer,
    outPlayer,
    position,
  }: {
    side: TeamSide;
    inPlayer: LineUpPlayer;
    outPlayer?: LineUpPlayer;
    position: number | LineupPositions;
  }) => {
    const body = {
      in: toLineupEntry(inPlayer, position),
      out: outPlayer && (await toStoredOutEntry(side, outPlayer, position)),
      side,
    };
    await postGameByGameIdSubstitute({
      path: { game_id: requireGameId() },
      body,
      throwOnError: true,
    });
  };

  /**
   * Restates both teams' whole lineups: the batting orders given, and the
   * pitchers and catchers omnibus stores (toLineupPair). Always both: omnibus clears every substitution on a lineup save, so a
   * team left out would revert to its starting lineup, in omnibus and in the
   * game service it relays the save to (`lineupUpdate`).
   */
  const saveLineups = async (lineUps: Record<TeamSide, LineUpPlayer[]>) => {
    const gameId = requireGameId();
    // Throws rather than saving without them: the save would drop both
    // teams' pitchers and catchers
    const stored = await fetchStoredLineups();
    const body = { lineups: toLineupPair(lineUps, stored) };
    // Omnibus edits whichever game is active on the machine, not one by id
    const { data } = await postGameActiveLineups({
      query: { machineUuid },
      body,
      throwOnError: true,
    });
    if (data.game.gameUuid !== gameId) {
      console.warn(
        `[lineups] saved to game ${data.game.gameUuid}, not ${gameId}: a different game is active on the machine`,
      );
    }
  };

  /**
   * Player details: name/handedness on the org player, jersey number on the
   * team roster's member entry.
   */
  const editPlayer = async (
    side: TeamSide,
    player: LineUpPlayer,
    edit: PlayerEdit,
  ) => {
    const roster = requireRoster(side);

    await postOrganizationsByOrgIdPlayersByPlayerExternalIdEdit({
      path: {
        // The roster's owning org, where its players live
        org_id: removeOrgPrefix(roster.orgId),
        player_external_id: encodeURIComponent(player.playerId),
      },
      body: {
        firstName: edit.firstName.trim(),
        lastName: edit.lastName.trim(),
        bats: toSideType(edit.hitSide),
        throws: toSideType(edit.pitchSide),
      },
      throwOnError: true,
    });

    const members = roster.team.unofficial_members ?? [];
    const isMember = members.some(
      (m) => getMemberExternalId(m) === player.playerId,
    );
    const isJerseyChanged =
      (edit.jerseyNumber ?? "") !== (player.jerseyNumber ?? "");
    if (isMember && isJerseyChanged) {
      await postRostersByRosterIdEdit({
        path: { roster_id: roster.rosterId },
        body: {
          unofficialMembers: members.map((member) =>
            getMemberExternalId(member) === player.playerId
              ? {
                  ...member,
                  jerseyNumber: edit.jerseyNumber
                    ? parseInt(edit.jerseyNumber, 10)
                    : null,
                }
              : member,
          ),
        },
        throwOnError: true,
      });
    }

    await invalidateRosters();
  };

  /**
   * Changes the hitter and/or pitcher of the active at-bat with a player tag,
   * so it works before any of its pitches is tagged.
   */
  const tagActivePlayers = async (
    data: GameFullResponse,
    overrides: TagPlayerOverrides,
  ) => {
    requireGameId();
    await sendTag(buildPlayerTagPayload(data, machineUuid, overrides));
  };

  /**
   * Re-tags a PA's last tagged pitch (Post At-Bat Update's edited results)
   * with the given hitter/pitcher.
   * TODO: the game service ignores players on pitch tags today, so they only
   * reach omnibus until it applies them to the PA.
   */
  const retagLastPitch = async (
    data: GameFullResponse,
    plateAppearanceId: string,
    overrides: TagPlayerOverrides,
  ) => {
    const body = buildLastPitchRetag(
      data,
      plateAppearanceId,
      machineUuid,
      overrides,
    );
    if (!body) throw new Error("This at bat has no tagged pitch to update");
    await sendTag(body);
  };

  /**
   * A tag queue send: a tagged pitch re-tagged as is, or an untagged one
   * filled, with the given hitter/pitcher.
   */
  const sendQueuedTag = async (
    data: GameFullResponse,
    activityId: string,
    action: "retag" | "fill",
    overrides: TagPlayerOverrides,
  ) => {
    const activity = data.activities.find((a) => a.activityId === activityId);
    const build = action === "retag" ? buildTagPayload : buildPlayerFillPayload;
    const body = activity && build(data, activity, machineUuid, overrides);
    if (!body) throw new Error(`Nothing to ${action} for this pitch`);
    await sendTag(body);
  };

  /**
   * Tags a pitch: `data` must already carry the new tags (the payload is
   * built from the pitch's result and resulting state). `stateBefore` is the
   * state it was thrown into, read before the tags were applied.
   */
  const tagPitch = async (
    data: GameFullResponse,
    activityId: string,
    stateBefore: ResultingState | null,
  ) => {
    const activity = data.activities.find((a) => a.activityId === activityId);
    const body =
      activity && buildTagPayload(data, activity, machineUuid, {}, stateBefore);
    if (!body) throw new Error("This pitch has nothing to tag");
    await sendTag(body);
  };

  /**
   * Corrects the live game state with a state tag. The game service applies
   * it and sends the result back over its WebSocket.
   */
  const tagGameState = async (data: GameFullResponse, edit: GameStateEdit) => {
    requireGameId();
    await sendTag(buildStateTagPayload(data, machineUuid, edit));
  };

  /** Ends the game, like the V1 tagger's End Game. */
  const endGame = async () => {
    await postGameByGameIdEnd({
      path: { game_id: requireGameId() },
      body: {},
      throwOnError: true,
    });
  };

  return {
    canWrite: Boolean(gameId),
    endGame,
    tagPitch,
    tagGameState,
    tagActivePlayers,
    ensureOnRoster,
    substitute,
    saveLineups,
    editPlayer,
    retagLastPitch,
    sendQueuedTag,
  };
};
